import * as React from "react";
import { ComponentTypes, DirectoryTreeType } from "../../../config/config";
import { Loader } from "../../Loader";
import { connect, ConnectedProps } from "react-redux";
import { addWorkingDirectory, setWorkingDirectories } from "../../../redux/modules/workingDirectory";
import { addRegionalSettings } from "../../../redux/modules/regional";
import { RootState } from "../../../redux";
import { ActionButton, Label, Spinner, SpinnerSize, TooltipHost } from "@fluentui/react";
import { initialFolderOptionPanelInfoSetting, SharePointFolderContentTypeIds, validSaveModes } from "../../../config/constants";
import { IFolderPanelInfo } from "../../../interfaces/IFolderPanelInfo";
import FolderOptionPanel from "../../panels/FolderOptionPanel";
import { FolderTypeEnum } from "../../enums/FolderTypeEnum";
import { LanguageContext } from "../../../../../utilities/LocalizationModule";
import { expanderConstants } from "../../../redux/models/constants/expanders";
import { setSearch } from "../../../redux/modules/search";
import ExpanderWithSubtitle from "../ExpanderWithSubtitle";
import FileAndFoldersSharepointSearch from "../../file/fileAndFoldersSharepointSearch";
import { ISiteInfoLocation, mapSites } from "../../../interfaces/ISiteInfo";
import authModule from "../../../../../utilities/AuthModule";
import { SharepointSpServices } from "../../../services/SharepointSpServices";
import { IPageSettings } from "../../../interfaces/IPageSettings";
import { isMobile } from "../../../styles/mobileStyles";
import { getHubSiteSites, getItemCustomSelect, getSharePointDocumentLibraries, getSharePointSubsite, getTeamChannels } from "../../../services/GraphService";
import { logger } from "../../../../../utilities/logger";
import generalModule from "../../../../../utilities/GeneralModule";
import { setContentManagerSearchValue, setIsContentManagerContentLoading, toggleContentManagerReload } from "../../../redux/modules/contentManagerDirectory";
import { ITeamChannelDto, ITeamChannelLocationDto } from "../../../interfaces/services/ITeamChannelDto";
import { IFolderLocation, ISharepointDocumentLocation } from "../../../interfaces/ISharepointDocument";
import { buildWorkingDirectoryFromTree, calculateLocationHeight, findWorkingDirectories, generateRandomString, openTreeFolderOptions } from "../../helpers/treeHelperGeneral";
import { SharePointDocumentLibraryItem } from "../../_v2/components/Explorer/FolderView/SharePointDocumentLibraryItem";
import { FolderViewWrapper } from "../../helpers/folderViewWrapper";
import { getListSettings, getUserEffectivePermissions } from "../../../services/SharePointRestServices";
import { setCanViewFolder } from "../../../redux/modules/currentSharepointFolder";
import { decrementAmountOfLoaders, incrementAmountOfLoaders } from "../../../redux/modules/loader";
import savingLocationStructureModule from "../../../../../utilities/SavingLocationStructureModule";
import { TeamChannelItem } from "../../_v2/components/Explorer/FolderView/TeamChannelItem";
import { useLazyRenderListAsStreamTreeQuery } from "../../_v2/redux/api/sharepoint/api-sharepoint-slice";
import { SharePointFolderItem } from "../../_v2/components/Explorer/FolderView/SharePointFolderItem";
import { SiteItem } from "../../_v2/components/Explorer/FolderView/SiteItem";
import { SharePointSiteHelper } from "../../../../../utilities/SharePointSiteHelper";
import SubTitleRefreshButton from "../SubTitleRefreshButton";
import { getStatusText, getTessAccountProjects, ITessAccountLocation, ITessProject, ITessProjectFolderLocation, ITessProjectLocation, TessProjectStatuses } from "../../../services/TessService";
import { TessAccountItem } from "../../_v2/components/Explorer/FolderView/TessAccountItem";
import { TessProjectFolderItem } from "../../_v2/components/Explorer/FolderView/TessProjectFolderItem";
import { ITessAuthHook } from "../../../../../contentManager/Hooks/useTessPopUpAuth";
import { TessProjectTreeItem } from "../../_v2/components/Explorer/FolderView/TessProjectTreeItem";
import { tryDecodeUriComponent } from "../../../../../utilities/EncodeDecode";
import { iconStyles } from "../../../styles/iconStyles";

const mapDispatchToProps = {
  addWorkingDirectory,
  setWorkingDirectories,
  setSearch,
  addRegionalSettings,
  setContentManagerSearchValue,
  setIsContentManagerContentLoading,
  incrementAmountOfLoaders,
  setCanViewFolder,
  decrementAmountOfLoaders,
  toggleContentManagerReload
};

const mapStateToProps = (state: RootState) => {
  return {
    amountOfLoaders: state.loading.amountOfLoaders,
    reload: state.reload.toggle,
    searchValue: state.search.searchValue.value,
    sites: state.sharePointInfo.sites,
    isSitesLoading: state.sharePointInfo.isSitesLoading,
    sitesPageData: state.sharePointInfo.pageData,
    teams: state.teams.teams,
    regionalSettings: state.regionalSettings,
    allowedSiteCollections: state.tenantInfo.allowedSiteCollections,
    hideTeamsSitesInSharePoint: state.tenantInfo?.hideTeamsSitesInSharePoint,
    hideDocumentLibrariesInTeams: state.tenantInfo?.hideDocumentLibrariesInTeams,
    userSettings: state.user.settings,
    contentManagerFilterValue: state.contentManagerDirectory.filterValue,
    user: state.user,
    mode: state.mode.mode,
    hasFileSelected: state.files.files.some(file => file.isSelected)
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface TessAccountsProps extends PropsFromRedux {
  hide: boolean;
  tessAuthHook: ITessAuthHook;
  isGeneralSearchRequest?: boolean;
  isSearchingFilesAndFolders?: boolean;
  hideGeneralSearchElement?: () => void;
}

const TessAccounts: React.FunctionComponent<TessAccountsProps> = ({
  hide,
  addWorkingDirectory,
  setWorkingDirectories,
  setSearch,
  reload,
  teams,
  searchValue,
  regionalSettings,
  addRegionalSettings,
  allowedSiteCollections,
  userSettings,
  contentManagerFilterValue,
  setContentManagerSearchValue,
  setIsContentManagerContentLoading,
  user,
  mode,
  hasFileSelected,
  incrementAmountOfLoaders,
  setCanViewFolder,
  decrementAmountOfLoaders,
  toggleContentManagerReload,
  hideTeamsSitesInSharePoint,
  hideDocumentLibrariesInTeams,
  isGeneralSearchRequest,
  isSearchingFilesAndFolders,
  hideGeneralSearchElement,
  tessAuthHook
}: TessAccountsProps) => {
  const languageStrings = React.useContext(LanguageContext);

  const [folderOptionPanelInfo, setFolderOptionPanelInfo] = React.useState<IFolderPanelInfo>(initialFolderOptionPanelInfoSetting);
  const [isSearching, setIsSearching] = React.useState<boolean>(false);
  const [isSearchingContent, setIsSearchingContent] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isReloading, setIsReloading] = React.useState<boolean>(false);
  const [isFileOrFolderSearchResultsAvailable, setIsFileOrFolderSearchResultsAvailable] = React.useState<boolean>(false);
  const [accountItems, setAccountItems] = React.useState<JSX.Element[]>([]);
  const [locationHeight, setLocationHeight] = React.useState<number>(160);
  const [tessAccountLocations, setTessAccountLocations] = React.useState<ITessAccountLocation[]>([]);

  const isFirstRun = React.useRef(true);
  const selectedItem = React.useRef(null);
  const allowSiteScroll = React.useRef<boolean>(false);

  const [getListAsStreamTree] = useLazyRenderListAsStreamTreeQuery();

  React.useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    (async () => {
      if (tessAuthHook) {
        setIsLoading(true);
        await tessAuthHook?.loadTessAccounts(true);
        setIsLoading(false);
      }
    })();
  }, [reload]);

  React.useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const tessAccountLocations = tessAuthHook?.tessAccountSites?.map(accountSite => {
          const randomString: string = generateRandomString();
          return {
            ...accountSite,
            accountId: accountSite.accountId,
            isSubsite: false,
            key: randomString,
            randomId: randomString,
            canExpand: true,
            expanded: false,
            treeType: DirectoryTreeType.tessAccount,
            level: 1,
            isLoading: false,
            loaded: false,
            isSelected: false
          };
        });

        setTessAccountLocations(tessAccountLocations?.sort(SharePointSiteHelper.sortSites) ?? []);

      } catch (error) {
        logger(error, "TessAccounts.tsx (onLocationTessAccountChevronClicked)");
      }
      setIsLoading(false);
    })();
  }, [tessAuthHook?.tessAccountSites]);

  React.useEffect(() => {
    if (!tessAccountLocations) return;

    let tessAccountsLocationResults: ITessAccountLocation[];
    if (searchValue && searchValue.trim() !== "") {
      tessAccountsLocationResults = tessAccountLocations.filter(account => account.name?.toLowerCase().includes(searchValue.trim().toLowerCase()));
    }
    else if (contentManagerFilterValue && contentManagerFilterValue.trim() !== "") {
      tessAccountsLocationResults = tessAccountLocations.filter(account => account.name?.toLowerCase().includes(contentManagerFilterValue.trim().toLowerCase()));
    }
    else {
      tessAccountsLocationResults = tessAccountLocations;
    }

    allowSiteScroll.current = (tessAccountsLocationResults as any[])?.some(site => site?.expanded
      && (site?.subSites?.some(x => x?.expanded)
        || site?.documents?.some(x => x?.expanded)
        || site?.folders?.some(x => x?.expanded)
        || site?.projectFolders?.some(x => x?.expanded)
        || site?.projects?.some(x => x?.expanded)
        || site?.channels?.some(x => x?.expanded)));

    setLocationHeight(calculateLocationHeight(tessAccountsLocationResults));
    setAccountItems(renderLocationTessAccount(tessAccountsLocationResults));
  }, [tessAccountLocations, searchValue, contentManagerFilterValue]);

  const getTreeLocations = React.useCallback((): any[] => {
    return tessAccountLocations;
  }, [tessAccountLocations]);

  const renderLocationTessAccount = (locationAccounts: ITessAccountLocation[]) => {
    return locationAccounts.map((location, index) => {
      return <div key={`${location.id}-${index}`}>
        <TessAccountItem
          item={location}
          isMobile={isMobile()}
          allowScroll
          onSiteClick={async (account: ITessAccountLocation) => {
            setLocationItem(account, true, undefined);
            const newWorkingDirectories = findWorkingDirectories(tessAccountLocations, account);
            const { directories } = await buildWorkingDirectoryFromTree(newWorkingDirectories, user);
            setWorkingDirectories(directories);
          }}
          onExpandCollapseClick={onLocationTessAccountChevronClicked}
          onOptionsClick={async (account: ITessAccountLocation) => setFolderOptionPanelInfo(await openTreeFolderOptions(tessAccountLocations, account, user))}
        />
        {location.documents && location.expanded && renderDocuments(location.documents)}
        {location.folders && location.expanded && renderFolders(location.folders, location)}
        {location.projectFolders && location.expanded && renderLocationTessProjectFolders(location.projectFolders)}
      </div>
    });
  };

  const renderLocationTessProjectFolders = (projectFolderLocations: ITessProjectFolderLocation[]) => {
    return projectFolderLocations.map((location, index) => {
      return <div key={`${location.name}-${index}`}>
        <TessProjectFolderItem
          item={location}
          isMobile={isMobile()}
          allowScroll
          onProjectFolderClick={async (projectFolder: ITessProjectFolderLocation) => {
            onLocationTessProjectFolderChevronClicked(projectFolder);
          }}
          onExpandCollapseClick={onLocationTessProjectFolderChevronClicked}
        />
        {location.projects && location.expanded && renderLocationTessProjects(location.projects)}
      </div>
    });
  };

  const renderLocationTessProjects = (projectLocations: ITessProjectLocation[]) => {
    return projectLocations.map((location, index) => {
      return <div key={`${location.name}-${index}`}>
        <TessProjectTreeItem
          item={location}
          isMobile={isMobile()}
          allowScroll
          onProjectClick={async (project: ITessProjectLocation) => {
            setLocationItem(project, true, undefined);
            const newWorkingDirectories = findWorkingDirectories(tessAccountLocations, project);
            const { directories } = await buildWorkingDirectoryFromTree(newWorkingDirectories, user);
            setWorkingDirectories(directories);
          }}
          onOptionsClick={async (project: ITessProjectLocation) => setFolderOptionPanelInfo(await openTreeFolderOptions(tessAccountLocations, project, user))}
          onExpandCollapseClick={onLocationTessProjectChevronClicked}
        />
        {location.documents && location.expanded && renderDocuments(location.documents)}
        {location.folders && location.expanded && renderFolders(location.folders, location)}
      </div>
    });
  };

  const onLocationTessAccountChevronClicked = async (location: ITessAccountLocation, isRefresh: boolean = false) => {
    if (isRefresh || (!location.expanded && !location.loaded)) {
      setLocationItem(location, undefined, true);
      try {
        const accessToken = await authModule.getGraphAccessToken();

        const projectFolders = await getTessAccountProjects(location.accountId);

        const documentLibraries: ISharepointDocumentLocation[] = [];
        const projectAccountFolders: IFolderLocation[] = [];
        const projectFolderLocations: ITessProjectFolderLocation[] = [];
        if (projectFolders) {
          if (projectFolders.account?.dossierMetadata?.length > 0) {
            await Promise.all(projectFolders.account?.dossierMetadata
              .filter(dossier => dossier.driveId && !dossier.itemId)
              .map(async dossier => {
                const documentLibraryData = await getItemCustomSelect(accessToken, dossier.driveId, null, "id,name,webUrl,sharepointIds,parentReference");
                if (documentLibraryData) {
                  documentLibraries.push({
                    id: documentLibraryData.id,
                    name: dossier.name,
                    webUrl: documentLibraryData.webUrl,
                    siteUrl: documentLibraryData.sharePointIds.siteUrl,
                    siteId: documentLibraryData.parentReference?.siteId ?? documentLibraryData.sharePointIds.siteId,
                    sharePointListId: documentLibraryData.sharePointIds.listId,
                    parentId: location.id,
                    treeType: DirectoryTreeType.document,
                    level: location.level + 1,
                    canExpand: true,
                    expanded: false,
                    isLoading: false,
                    randomId: generateRandomString()
                  });
                }
              }));

            await Promise.all(projectFolders.account?.dossierMetadata
              .filter(dossier => dossier.driveId && dossier.itemId)
              .map(async dossier => {
                const folderData = await getItemCustomSelect(accessToken, dossier.driveId, dossier.itemId, "id,name,folder,webDavUrl,sharepointIds");
                if (folderData) {
                  projectAccountFolders.push({
                    id: folderData.id,
                    name: dossier.name,
                    level: location.level + 1,
                    driveId: dossier.driveId,
                    sharePointUniqueId: folderData.sharepointIds.listItemUniqueId,
                    contentTypeId: SharePointFolderContentTypeIds.folder,
                    path: tryDecodeUriComponent(new URL(folderData.webDavUrl).pathname),
                    canExpand: folderData.folder?.childCount > 0,
                    expanded: false,
                    isLoading: false,
                    webUrl: folderData.sharepointIds.siteUrl,
                    sharePointListId: folderData.sharepointIds.listId,
                    treeType: DirectoryTreeType.folder,
                    randomId: generateRandomString(),
                    canRefresh: true
                  } as any);
                }
              }));
          }

          TessProjectStatuses.forEach(status => {
            let projects: ITessProject[] = projectFolders[status.key];
            if (Array.isArray(projects) && projects.length > 0) {
              const randomString: string = generateRandomString();
              projectFolderLocations.push({
                id: randomString,
                name: getStatusText(languageStrings, status.key),
                treeType: DirectoryTreeType.tessProjectStatusContainer,
                level: location.level + 1,
                canExpand: true,
                expanded: false,
                isLoading: false,
                randomId: randomString,
                projects: projects.map(project => ({ ...project, accountId: location.accountId }))
              });
            }
          });
        }

        setLocationsSiteSubsiteUpdate(location, undefined, documentLibraries, projectAccountFolders, undefined, false, isRefresh, undefined, undefined, projectFolderLocations);
      } catch (error) {
        logger(error, "TessAccounts.tsx (onLocationTessAccountChevronClicked)");
        setLocationItem(location, undefined, false);
      }
    }
    else if (location.expanded || location.documents?.length > 0 || location.projectFolders?.length > 0) {
      setLocationsToggleExpanded(location);
    }
  };

  const onLocationTessProjectFolderChevronClicked = async (location: ITessProjectFolderLocation, isRefresh: boolean = false) => {
    if (isRefresh || (!location.expanded && !location.loaded)) {
      setLocationItem(location, undefined, true);
      try {
        const projectSites = (await Promise.all(location.projects.map(async project => {
          let projectLocation: ITessProjectLocation;
          if (Array.isArray(project.dossierMetadata) && project.dossierMetadata.length > 0) {
            projectLocation = {
              ...project,
              tessProjectId: project.id,
              randomId: generateRandomString(),
              canExpand: true,
              expanded: false,
              treeType: DirectoryTreeType.tessProject,
              level: location.level + 1,
              isLoading: false,
              loaded: false,
              isSelected: false
            };
          }
          return projectLocation;
        })))
          .filter(x => x);

        setLocationsSiteSubsiteUpdate(location, undefined, undefined, undefined, undefined, false, isRefresh, undefined, undefined, undefined, projectSites);

      } catch (error) {
        logger(error, "TessAccounts.tsx (onLocationTessProjectFolderChevronClicked)");
        setLocationItem(location, undefined, false);
      }
    }
    else if (location.expanded || (location.projects?.length > 0)) {
      setLocationsToggleExpanded(location);
    }
  };

  const onLocationTessProjectChevronClicked = async (location: ITessProjectLocation, isRefresh: boolean = false) => {
    if (isRefresh || (!location.expanded && !location.loaded)) {
      setLocationItem(location, undefined, true);
      try {
        const accessToken = await authModule.getGraphAccessToken();

        const documentLibraries: ISharepointDocumentLocation[] = [];
        const projectAccountFolders: IFolderLocation[] = [];

        if (location?.dossierMetadata?.length > 0) {
          await Promise.all(location.dossierMetadata
            .filter(dossier => dossier.driveId && !dossier.itemId)
            .map(async dossier => {
              const documentLibraryData = await getItemCustomSelect(accessToken, dossier.driveId, null, "id,name,webUrl,sharepointIds,parentReference");
              if (documentLibraryData) {
                documentLibraries.push({
                  id: documentLibraryData.id,
                  name: dossier.name,
                  webUrl: documentLibraryData.webUrl,
                  siteUrl: documentLibraryData.sharePointIds.siteUrl,
                  siteId: documentLibraryData.parentReference?.siteId ?? documentLibraryData.sharePointIds.siteId,
                  sharePointListId: documentLibraryData.sharePointIds.listId,
                  parentId: location.id,
                  treeType: DirectoryTreeType.document,
                  level: location.level + 1,
                  canExpand: true,
                  expanded: false,
                  isLoading: false,
                  randomId: generateRandomString()
                });
              }
            }));

          await Promise.all(location.dossierMetadata
            .filter(dossier => dossier.driveId && dossier.itemId)
            .map(async dossier => {
              const folderData = await getItemCustomSelect(accessToken, dossier.driveId, dossier.itemId, "id,name,folder,webDavUrl,sharepointIds");
              if (folderData) {
                projectAccountFolders.push({
                  id: folderData.id,
                  name: dossier.name,
                  level: location.level + 1,
                  driveId: dossier.driveId,
                  sharePointUniqueId: folderData.sharepointIds.listItemUniqueId,
                  contentTypeId: SharePointFolderContentTypeIds.folder,
                  path: tryDecodeUriComponent(new URL(folderData.webDavUrl).pathname),
                  canExpand: folderData.folder?.childCount > 0,
                  expanded: false,
                  isLoading: false,
                  webUrl: folderData.sharepointIds.siteUrl,
                  sharePointListId: folderData.sharepointIds.listId,
                  treeType: DirectoryTreeType.folder,
                  randomId: generateRandomString(),
                  canRefresh: true
                } as any);
              }
            }));
        }

        setLocationsSiteSubsiteUpdate(location, undefined, documentLibraries, projectAccountFolders, undefined, false, isRefresh, undefined);
      } catch (error) {
        logger(error, "TessAccounts.tsx (onLocationTessProjectChevronClicked)");
        setLocationItem(location, undefined, false);
      }
    }
    else if (location.expanded || location.documents?.length > 0 || location.folders?.length > 0) {
      setLocationsToggleExpanded(location);
    }
  };

  const UpdateIsFileOrFolderSearchResultsAvailable = (resultsAvailable: boolean) => {
    setIsFileOrFolderSearchResultsAvailable(resultsAvailable);
  }

  const openFolderOptions = (site: any) => {
    const teamSite = teams?.find(team => team.siteId === site.id || team.siteId === site.siteId);

    let locations: IPageSettings[];
    if (isGeneralSearchRequest || generalModule.isInConentMangerMode()) {
      locations = [
        {
          type: ComponentTypes.SharePoint,
          id: "1",
          directory: "",
          displayName: ComponentTypes.SharePoint,
          webUrl: "",
        },
        {
          id: teamSite && teamSite.id,
          type: ComponentTypes.SharePointDocuments,
          siteId: !teamSite ? site.id : teamSite.siteId,
          siteDisplayName: !teamSite ? site.name : teamSite.displayName,
          webUrl: !teamSite ? site.webUrl : teamSite.webUrl,
          directory: "",
          DocumentLibrary: "",
          teamGroupId: teamSite && teamSite.id,
          displayName: !teamSite ? site.name : teamSite.displayName,
          isSharedTeam: teamSite?.isSharedTeam
        }
      ]
    }

    if (teamSite) {
      setFolderOptionPanelInfo({
        isOpen: true,
        folder: { driveId: null, name: teamSite.displayName, id: teamSite.id, siteId: teamSite.siteId, isSharedTeam: teamSite.isSharedTeam },
        folderType: FolderTypeEnum.teamMainSite,
        webUrl: teamSite.webUrl,
        locations: locations
      });
    } else {
      setFolderOptionPanelInfo({
        isOpen: true,
        folder: { driveId: null, name: site.name, id: site.id },
        folderType: site.isHub ? FolderTypeEnum.sharepointHubSite : FolderTypeEnum.sharepointSite,
        webUrl: site.webUrl,
        locations: locations
        // isTeamsSiteInsideSharepoint: teams?.some(team => team.siteId === site.id)
      });
    }
  };

  const addDirectory = React.useCallback(async (item: ISiteInfoLocation) => {
    let directoryToAdd: IPageSettings = {
      id: item.id,
      type: ComponentTypes.Tess,
      siteId: item.siteId ?? item.id,
      siteDisplayName: item.name,
      webUrl: item.webUrl,
      directory: "",
      DocumentLibrary: "",
      displayName: item.name,
      isHub: item.isHub,
      hubId: item.hubId,
      parentHubId: item.parentHubId
    };

    const teamSite = teams?.find(team => team.siteId === item.siteId || team.siteId === item.id);
    if (teamSite) {
      directoryToAdd.teamGroupId = teamSite.id;
      directoryToAdd.teamDriveId = teamSite.driveId;
      directoryToAdd.isSharedTeam = teamSite.isSharedTeam;
    }

    if (isGeneralSearchRequest || generalModule.isInConentMangerMode()) {
      if (generalModule.isInConentMangerMode()) setContentManagerSearchValue("");
      if (isGeneralSearchRequest && hideGeneralSearchElement) hideGeneralSearchElement();
      setWorkingDirectories([
        {
          type: ComponentTypes.SharePoint,
          id: "1",
          directory: "",
          displayName: ComponentTypes.SharePoint,
          webUrl: "",
        },
        directoryToAdd
      ]);
    }
    else {
      setSearch("");
      addWorkingDirectory(directoryToAdd);
    }

    if (!teamSite?.isSharedTeam) {
      await setupReduxRegionalSettings(item.webUrl);
    }
  }, [teams]);

  const setupReduxRegionalSettings = async (webUrl: string) => {
    const isAlreadySetup = regionalSettings[webUrl];
    if (!isAlreadySetup) {
      const accessToken = await authModule.getSharePointAccessToken();
      const sharepointSpService = new SharepointSpServices(webUrl, accessToken);
      const regionalSettings = await sharepointSpService.getSiteRegionalSettings();
      if (regionalSettings) {
        addRegionalSettings({ ...regionalSettings, webUrl: webUrl });
      }
    }
  };

  const setContextAwareLoading = React.useCallback((isLoading: boolean, contentManagerOnly: boolean = false) => {
    if (!contentManagerOnly) {
      !generalModule.isInConentMangerMode()
        ? isLoading ? incrementAmountOfLoaders() : decrementAmountOfLoaders()
        : setIsContentManagerContentLoading(isLoading);
    }
    else {
      setIsContentManagerContentLoading(isLoading);
    }
  }, []);

  const onTreeItemClick = React.useCallback(async (event: React.SyntheticEvent, treeItem: any) => {
    event.stopPropagation();
    setContextAwareLoading(true);
    try {
      setLocationItem(treeItem, true, undefined);
      const newWorkingDirectories = findWorkingDirectories(tessAccountLocations, treeItem);
      const { directories } = await buildWorkingDirectoryFromTree(newWorkingDirectories, user);
      if (!generalModule.isInConentMangerMode()) setSearch("");
      setWorkingDirectories(directories);
      if (generalModule.isInConentMangerMode()) {
        setContentManagerSearchValue("");
        toggleContentManagerReload();
      }
    } catch (error) {
      logger(error, "TessAccounts.tsx (onTreeItemClick)");
    }
    setContextAwareLoading(false);
  }, [tessAccountLocations, user]);

  const renderLocationSites = (locationSites: ISiteInfoLocation[]) => {
    return locationSites
      ? SharePointSiteHelper.groupAndSortSites(locationSites)
        .map(item => {
          const isTeamSite = teams?.some(team => team.siteId === item.id || team.siteId === item.siteId);
          return isTeamSite && (!userSettings.enableTeams || hideTeamsSitesInSharePoint) ? null : (
            <div key={item.id}>
              <SiteItem
                item={item}
                isTeamsLocation={false}
                isMobile={isMobile()}
                allowScroll={allowSiteScroll.current}
                onSiteClick={addDirectory}
                onExpandCollapseClick={onLocationSitesChevronClicked}
                onOptionsClick={openFolderOptions}
              />
              {item.subSites && item.expanded && renderLocationSites(item.subSites)}

              {item.subSitesNextHref && <div style={{ paddingLeft: (item.level ? item.level + 1 : 1) * 15 + 20 }}>
                {!item.isLoading && <ActionButton iconProps={{ iconName: "Table" }} allowDisabledFocus onClick={async (e: any) => {
                  e.stopPropagation();
                  setLocationItem(item, undefined, true);

                  try {
                    const level = item.level ? item.level : 0;

                    let accessToken = await authModule.getGraphAccessToken();
                    let subSiteResult = await getSharePointSubsite(accessToken, item.id, allowedSiteCollections, item.subSitesNextHref);

                    const subsites: ISiteInfoLocation[] = subSiteResult?.data?.map(subsite => {
                      return {
                        id: subsite.id,
                        name: subsite.displayName,
                        description: subsite.description,
                        webUrl: subsite.webUrl,
                        isSubsite: true,
                        canExpand: true,
                        expanded: false,
                        parentId: item.id,
                        treeType: DirectoryTreeType.site,
                        level: level + 1,
                        isLoading: false,
                        loaded: false,
                        isSelected: false,
                        randomId: generateRandomString()
                      }
                    })

                    setLocationsSiteSubsiteUpdate(item, [...item.subSites, ...subsites], item.documents, undefined, undefined, true, false, subSiteResult.nextPageLink);

                  } catch (error) {
                    logger(error, "TessAccounts.tsx (subsites load more (getSharePointSubsite))");
                    setLocationItem(location, undefined, false);
                  }
                }}>
                  {languageStrings.LoadMore}
                </ActionButton>}

              </div>}
              {item.subSitesNextHref && item.isLoading && <div style={{ paddingTop: "10px" }}>
                <Spinner size={SpinnerSize.small} />
              </div>}

              {item.channels && item.expanded && renderChannels(item.channels)}
              {item.documents && item.expanded && renderDocuments(item.documents)}
            </div>
          )
        }).filter(item => item)
      : (!searchValue && !isSearching
        ? [<div key="0">
          <p className="xb-empty-location-message centerText">{contentManagerFilterValue?.length > 0 ? languageStrings.NoResultsFound : languageStrings.NotAssignedToSite}</p>
        </div>]
        : [])
  };

  const onLocationSitesChevronClicked = async (item: ISiteInfoLocation, isRefresh: boolean = false) => {
    setLocationItem(item, undefined, true);
    try {
      if (isRefresh || (!item.expanded && !item.loaded)) {
        const level = item.level;

        const accessToken = await authModule.getGraphAccessToken();
        let [subSitesResult, documentLibrariesResult, channelsResult, hubSitesResult] = await Promise.all([
          !item.isSharedTeam ? getSharePointSubsite(accessToken, item.isTeam ? item.siteId : item.id, allowedSiteCollections) : { data: [], nextPageLink: null },
          !item.isSharedTeam && (!hideDocumentLibrariesInTeams || !item.isTeam) ? getSharePointDocumentLibraries(accessToken, item.isTeam ? item.siteId : item.id) : [],
          item.isTeam && getTeamChannels(accessToken, item.teamGroupId, item.teamDriveId),
          item.isHub && getHubSiteSites(accessToken, item.hubId, allowedSiteCollections)
        ]);

        let subSites: ISiteInfoLocation[] = (subSitesResult?.data ?? [])
          .concat(hubSitesResult ?? [])
          .map(subSite => {
            return {
              ...subSite,
              name: subSite.displayName,
              isSubsite: !subSite.isHub,
              parentId: item.id,
              treeType: DirectoryTreeType.site,
              canExpand: true,
              expanded: false,
              level: level + 1,
              isLoading: false,
              randomId: generateRandomString()
            }
          });

        const documents: ISharepointDocumentLocation[] = documentLibrariesResult?.map(doc => {
          return {
            ...doc,
            parentId: item.id,
            treeType: DirectoryTreeType.document,
            level: level + 1,
            canExpand: true,
            expanded: false,
            isLoading: false,
            randomId: generateRandomString()
          }
        });

        const channels: ITeamChannelLocationDto[] = channelsResult?.map(channel => {
          return {
            ...channel,
            parentId: item.id,
            treeType: DirectoryTreeType.channel,
            isSelected: false,
            key: item.key,
            level: level + 1,
            randomId: generateRandomString(),
            canExpand: true,
            expanded: false,
            loaded: false
          }
        });

        setLocationsSiteSubsiteUpdate(item, subSites, documents, undefined, undefined, false, isRefresh, subSitesResult.nextPageLink, channels);
      }
      else if ((!item.expanded && (item.documents?.length > 0 || item.channels?.length > 0 || item.subSites?.length > 0)) || item.expanded) {
        setLocationsToggleExpanded(item);
      }
    } catch (error) {
      logger(error, "TessAccounts.tsx (renderLocationSites)");
      setLocationItem(item, undefined, false);
    }
  }

  const setLocationsToggleExpanded = (location: any) => {
    setTessAccountLocations(prevLocations => {
      const recursiveMap = (sites) => {
        return sites.map(site => {
          if (site.id === location.id && site.randomId === location.randomId) {
            return {
              ...site,
              expanded: !site.expanded,
              isLoading: false,
              isLoadingChannelInfo: false,
            };
          }
          let updatedSite = site;
          if (site.subSites) {
            updatedSite = {
              ...updatedSite,
              subSites: recursiveMap(site.subSites),
            };
          }
          if (site.documents) {
            updatedSite = {
              ...updatedSite,
              documents: recursiveMap(site.documents),
            };
          }
          if (site.channels) {
            updatedSite = {
              ...updatedSite,
              channels: recursiveMap(site.channels),
            };
          }
          if (site.folders) {
            updatedSite = {
              ...updatedSite,
              folders: recursiveMap(site.folders),
            };
          }
          if (site.accounts) {
            updatedSite = {
              ...updatedSite,
              accounts: recursiveMap(site.accounts),
            };
          }
          if (site.projectFolders) {
            updatedSite = {
              ...updatedSite,
              projectFolders: recursiveMap(site.projectFolders),
            };
          }
          if (site.projects) {
            updatedSite = {
              ...updatedSite,
              projects: recursiveMap(site.projects),
            };
          }
          return updatedSite;
        });
      }

      return recursiveMap(prevLocations);
    });
  }

  const setLocationItem = (location: any, isSelected?: boolean, isLoading?: boolean, isLoadingChannelInfo?: boolean) => {
    setTessAccountLocations((prevLocations) => {
      if (isSelected !== undefined) {
        selectedItem.current = { id: location.id, randomId: location.randomId };
      }
      const recursiveMap = (sites) => {
        return sites.map((site: any) => {
          let updatedSite = site;
          let isCurrentItemSelected =
            selectedItem.current && site.id === selectedItem.current.id && site.randomId === selectedItem.current.randomId;
          if (site.id === location.id && site.randomId === location.randomId) {
            updatedSite = {
              ...site,
              isLoading: isLoading !== undefined ? isLoading : site.isLoading,
              isSelected: isSelected !== undefined ? isSelected : site.isSelected,
              isLoadingChannelInfo: isLoadingChannelInfo !== undefined ? isLoadingChannelInfo : site.isLoadingChannelInfo,
            };
          }
          else if (isCurrentItemSelected) {
            updatedSite = {
              ...site,
              isSelected: isSelected !== undefined ? isSelected : site.isSelected
            };
          }
          else {
            updatedSite = {
              ...site,
              isSelected: false,
            };
          }
          if (site.subSites) {
            updatedSite = {
              ...updatedSite,
              subSites: recursiveMap(site.subSites),
            };
          }
          if (site.documents) {
            updatedSite = {
              ...updatedSite,
              documents: recursiveMap(site.documents),
            };
          }
          if (site.channels) {
            updatedSite = {
              ...updatedSite,
              channels: recursiveMap(site.channels),
            };
          }
          if (site.folders) {
            updatedSite = {
              ...updatedSite,
              folders: recursiveMap(site.folders),
            };
          }
          if (site.accounts) {
            updatedSite = {
              ...updatedSite,
              accounts: recursiveMap(site.accounts),
            };
          }
          if (site.projectFolders) {
            updatedSite = {
              ...updatedSite,
              projectFolders: recursiveMap(site.projectFolders),
            };
          }
          return updatedSite;
        });
      };

      return recursiveMap(prevLocations);
    });
  };

  const setLocationsSiteSubsiteUpdate = (location: any, subsites: ISiteInfoLocation[], documents: ISharepointDocumentLocation[], folders: IFolderLocation[], foldersNextHref: string, isLoadmore: boolean = false, isRefresh: boolean = false, subSitesNextHref: string, channels?: ITeamChannelDto[], projectFolders?: ITessProjectFolderLocation[], projects?: ITessProjectLocation[]) => {
    setTessAccountLocations(prevLocations => {
      const recursiveFolderUpdate = (items) => {
        return items?.map(item => {
          let updatedItem = item;
          if (item.id === location.id && item.randomId === location.randomId) {
            updatedItem = {
              ...item,
              folders: [...folders],
              foldersNextHref: foldersNextHref ? foldersNextHref : "",
              expanded: (isLoadmore || isRefresh) ? item.expanded : !item.expanded,
              canExpand: folders.length > 0,
              isLoading: false,
              loaded: true,
            };
          }
          else if (item.folders) {
            updatedItem = {
              ...updatedItem,
              folders: recursiveFolderUpdate(item.folders),
            };
          }
          return updatedItem;
        });
      }

      const recursiveUpdate = (sites) => {
        return sites.map(site => {
          if (site.id === location.id && site.randomId === location.randomId) {
            return {
              ...site,
              folders: Array.isArray(folders) ? [...folders] : [],
              subSites: subsites,
              channels: channels,
              subSitesNextHref: subSitesNextHref ? subSitesNextHref : "",
              documents: Array.isArray(documents) ? (isRefresh || isLoadmore) ? [...documents] : [...(site.documents || []), ...documents] : [],
              projectFolders: projectFolders,
              projects: projects,
              expanded: (isLoadmore || isRefresh) ? site.expanded : !site.expanded,
              isLoading: false,
              loaded: true,
            };
          }
          let updatedSite = site;
          if (site.subSites) {
            updatedSite = {
              ...updatedSite,
              subSites: recursiveUpdate(site.subSites),
            };
          }
          if (site.documents) {
            updatedSite = {
              ...updatedSite,
              documents: recursiveFolderUpdate(site.documents),
            };
          }
          if (site.folders) {
            updatedSite = {
              ...updatedSite,
              folders: recursiveFolderUpdate(site.folders),
            };
          }
          if (site.channels) {
            updatedSite = {
              ...updatedSite,
              channels: recursiveFolderUpdate(site.channels)
            };
          }
          if (site.accounts) {
            updatedSite = {
              ...site,
              accounts: recursiveUpdate(site.accounts),
            };
          }
          if (site.projectFolders) {
            updatedSite = {
              ...updatedSite,
              projectFolders: recursiveUpdate(site.projectFolders),
            };
          }
          if (site.projects) {
            updatedSite = {
              ...updatedSite,
              projects: recursiveUpdate(site.projects),
            };
          }
          return updatedSite;
        });
      }

      return recursiveUpdate(prevLocations);
    });
  }

  const setLocationsChannelUpdate = (location: any, channels: ITeamChannelLocationDto[], folders: IFolderLocation[], foldersNextHref: string, isLoadmore: boolean = false, isRefresh: boolean = false) => {
    setTessAccountLocations(prevLocations => {
      const recursiveFolderUpdate = (items) => {
        return items.map(item => {
          let updatedItem = item;
          if (item.id === location.id && item.randomId === location.randomId) {
            updatedItem = {
              ...item,
              folders: [...folders || []],
              foldersNextHref: foldersNextHref ? foldersNextHref : "",
              expanded: (isLoadmore || isRefresh) ? item.expanded : !item.expanded,
              canExpand: folders.length > 0,
              isLoading: false,
              loaded: true,
            };

            return updatedItem;
          }
          else if (item.folders) {
            updatedItem = {
              ...updatedItem,
              folders: recursiveFolderUpdate(item.folders),
            };
          }
          return updatedItem;
        });
      }

      return prevLocations.map(prevLocation => {
        if (prevLocation.id === location.id && prevLocation.randomId === location.randomId) {
          return {
            ...prevLocation,
            channels: channels,
            expanded: isRefresh ? prevLocation.expanded : !prevLocation.expanded,
            isLoading: false,
            loaded: true,
          };
        }
        if (prevLocation.channels) {
          return {
            ...prevLocation,
            channels: recursiveFolderUpdate(prevLocation.channels),
          };
        }
        else {
          return prevLocation;
        }
      });
    });
  }

  const isValidDropZone = React.useMemo((): boolean => {
    return validSaveModes.some(item => item === mode) || generalModule.isInConentMangerMode();
  }, [mode]);

  const handleSaveClick = React.useCallback(async (item, e) => {
    e.preventDefault();
    e.stopPropagation();
    e.persist();

    setContextAwareLoading(true);

    try {
      const newWorkingDirectories = findWorkingDirectories(tessAccountLocations, item);
      const { directories } = await buildWorkingDirectoryFromTree(newWorkingDirectories, user);
      const currentDir = directories[directories.length - 1];
      const parent = newWorkingDirectories.find(x => x.id === item.parentId);

      var folderViewWrapper = new FolderViewWrapper(parent.subSites, { ...currentDir, DocumentLibrary: item.name, sharePointListId: item.sharePointListId });
      const canViewFolder = folderViewWrapper.canViewFolder();
      const accessToken = await authModule.getSharePointAccessToken();
      const webUrl = currentDir?.webUrl;
      const [settings, permissions] = await Promise.all([
        getListSettings(accessToken, webUrl, item.sharePointListId),
        getUserEffectivePermissions(accessToken, webUrl, item.sharePointListId)
      ]);

      setCanViewFolder({
        sharepointListId: item.sharePointListId,
        canView: canViewFolder,
        canCreateFolder: canViewFolder ? settings?.EnableFolderCreation : false,
        contentTypesEnabled: settings?.ContentTypesEnabled,
        enableMinorVersions: settings?.EnableMinorVersions,
        disableOpenInApp: settings?.ExcludeFromOfflineClient === true,
        userPermissions: permissions
      });

      savingLocationStructureModule.setSavingLocationStructure(directories);

      if (generalModule.isInConentMangerMode()) {
        generalModule.setOpenPanelUploadPanel(true);
        setWorkingDirectories(directories);
        setContentManagerSearchValue("");
        toggleContentManagerReload();
      } else {
        const savingDirectory = {
          ...directories[directories.length - 1],
          isSaveLocationStructureSet: true,
          displayName: languageStrings.Properties,
          type: ComponentTypes.Saving
        }

        const newLocations = [
          ...directories,
          savingDirectory,
        ];

        setWorkingDirectories(newLocations);
      }
    } catch (error) {
      logger(error, "TaskpaneDialog.ts (handleSaveClick)");
    }

    setSearch("");
    setContextAwareLoading(false);
  }, [tessAccountLocations, user]);

  const onChannelSaveClicked = React.useCallback(async (event: React.SyntheticEvent, item: ITeamChannelLocationDto, isSaveRequest: boolean = false) => {
    if (isSaveRequest) {
      event.preventDefault();
      event.stopPropagation();

      setContextAwareLoading(true);

      try {
        const newWorkingDirectories = findWorkingDirectories(tessAccountLocations, item);
        const { directories } = await buildWorkingDirectoryFromTree(newWorkingDirectories, user);

        savingLocationStructureModule.setSavingLocationStructure(directories);

        if (generalModule.isInConentMangerMode()) {
          generalModule.setOpenPanelUploadPanel(true);
          setWorkingDirectories(directories);
        } else {
          const savingDirectory = {
            ...directories[directories.length - 1],
            isSaveLocationStructureSet: true,
            displayName: languageStrings.Properties,
            type: ComponentTypes.Saving
          }

          const newLocations = [
            ...directories,
            savingDirectory,
          ];

          setWorkingDirectories(newLocations);
        }
      } catch (error) {
        logger(error, "TessAccounts.tsx (onChannelSaveClicked)");
      }

      setContextAwareLoading(false);
    }
  }, [tessAccountLocations, user]);

  const onFolderSaveClicked = React.useCallback(async (item: any, e: any) => {
    e.preventDefault();
    e.stopPropagation();
    e.persist();

    setContextAwareLoading(true);

    try {
      const newWorkingDirectories = findWorkingDirectories(tessAccountLocations, item);
      const { directories } = await buildWorkingDirectoryFromTree(newWorkingDirectories, user);

      savingLocationStructureModule.setSavingLocationStructure(directories);

      if (generalModule.isInConentMangerMode()) {
        generalModule.setOpenPanelUploadPanel(true);
        setWorkingDirectories(directories);
      } else {
        const savingDirectory = {
          ...directories[directories.length - 1],
          isSaveLocationStructureSet: true,
          displayName: languageStrings.Properties,
          type: ComponentTypes.Saving
        }

        const newLocations = [
          ...directories,
          savingDirectory,
        ];

        setWorkingDirectories(newLocations);
      }
    } catch (error) {
      logger(error, "TessAccounts.tsx (onFolderSaveClicked)");
    }

    setContextAwareLoading(false);
  }, [tessAccountLocations, user]);

  const renderDocuments = (documents: ISharepointDocumentLocation[]) => {
    return documents.map((document, index) => (
      <div key={`${document.id}-${index}`}>
        <SharePointDocumentLibraryItem
          key={index}
          treeItem={document}
          isMobile={isMobile()}
          isValidDropZone={isValidDropZone}
          allowScroll
          getTreeLocations={getTreeLocations}
          hasFileSelected={hasFileSelected}
          handleSaveClick={handleSaveClick}
          onTreeItemClick={onTreeItemClick}
          openFolderOptionsTree={async (e) => {
            e.stopPropagation();
            setFolderOptionPanelInfo(await openTreeFolderOptions(tessAccountLocations, document, user));
          }}
          onTreeExpand={async (isRefresh) => {
            if (isRefresh || (!document.expanded && !document.loaded)) {
              setLocationItem(document, undefined, true);
              try {
                const newWorkingDirectories = findWorkingDirectories(tessAccountLocations, document);
                const { directories } = await buildWorkingDirectoryFromTree(newWorkingDirectories, user);
                const currDirectory = directories[directories.length - 1];

                const level = document.level;
                let folders: IFolderLocation[] = [];

                const results = await getListAsStreamTree({
                  siteUrl: currDirectory.webUrl,
                  sharePointListId: currDirectory.sharePointListId,
                  accessToken: await authModule.getSharePointAccessToken(),
                  nextPageUrl: "",
                  serverRelativePath: ""
                });

                const foldersNextHref = results?.data?.ListData?.NextHref ? results?.data?.ListData?.NextHref : "";

                if (results?.data?.ListData?.Row && results?.data?.ListData?.Row.length > 0) {
                  folders = results?.data?.ListData?.Row.map(row => {
                    const folderChildCount = row.FolderChildCount ? +row.FolderChildCount : 0;

                    const spItemUrlparts = row['.spItemUrl'].split('/');
                    const itemId = spItemUrlparts[spItemUrlparts.length - 1].split('?')[0];
                    const driveId = spItemUrlparts[spItemUrlparts.length - 3];

                    return {
                      id: itemId,
                      name: row.FileLeafRef,
                      level: level + 1,
                      driveId,
                      sharePointUniqueId: row.UniqueId.replace("{", "").replace("}", ""),
                      contentTypeId: row.ContentTypeId,
                      path: row.FileRef,
                      canExpand: folderChildCount > 0,
                      expanded: false,
                      isLoading: false,
                      webUrl: currDirectory.webUrl,
                      sharePointListId: currDirectory.sharePointListId,
                      treeType: DirectoryTreeType.folder,
                      randomId: generateRandomString(),
                      canRefresh: true
                    }
                  })
                }

                setLocationsSiteSubsiteUpdate(document, undefined, undefined, folders, foldersNextHref, false, isRefresh, undefined);

              } catch (error) {
                logger(error, "SharePointSite.ts (renderDocuments(getListAsStreamTree))");
              }
              setLocationItem(document, undefined, false);
            }
            else if ((!document.expanded && document.folders?.length > 0) || document.expanded) {
              setLocationsToggleExpanded(document);
            }
          }}
        />
        {document.folders && document.expanded && renderFolders(document.folders, document)}
      </div>
    ));
  };

  const renderChannels = (channels: ITeamChannelLocationDto[]) => {
    return channels.map((channel, index) => (
      <div key={`${channel.id}-${index}`}>
        <TeamChannelItem
          key={index}
          treeItem={channel}
          isMobile={isMobile()}
          isValidDropZone={isValidDropZone}
          hasFileSelected={hasFileSelected}
          allowScroll
          getTreeLocations={getTreeLocations}
          inSharepointComponent={true}
          onChannelClicked={onChannelSaveClicked}
          onTreeItemClick={onTreeItemClick}
          openFolderOptionsTree={async (e) => {
            e.stopPropagation();
            setLocationItem(channel, undefined, undefined, true);
            setFolderOptionPanelInfo(await openTreeFolderOptions(tessAccountLocations, channel, user));
            setLocationItem(channel, undefined, undefined, false);
          }}
          onTreeExpand={async (isRefresh) => {
            if (isRefresh || (!channel.expanded && !channel.loaded)) {
              setLocationItem(channel, undefined, true);
              try {
                const newWorkingDirectories = findWorkingDirectories(tessAccountLocations, channel);
                const { directories } = await buildWorkingDirectoryFromTree(newWorkingDirectories, user);
                const currDirectory = directories[directories.length - 1];

                const domain = new URL(currDirectory.webUrl).origin;

                const level = channel.level;
                let folders: IFolderLocation[] = [];

                const results = await getListAsStreamTree({
                  siteUrl: currDirectory.webUrl,
                  sharePointListId: currDirectory.sharePointListId,
                  accessToken: await authModule.getSharePointAccessToken(),
                  nextPageUrl: "",
                  serverRelativePath: currDirectory.directory ? `${currDirectory.webUrl.replace(domain, "")}/${currDirectory.directory}` : ""
                });

                const foldersNextHref = results?.data?.ListData?.NextHref ? results?.data?.ListData?.NextHref : "";

                if (results?.data?.ListData?.Row && results?.data?.ListData?.Row.length > 0) {
                  folders = results?.data?.ListData?.Row.map(row => {
                    const folderChildCount = row.FolderChildCount ? +row.FolderChildCount : 0;

                    const spItemUrlparts = row['.spItemUrl'].split('/');
                    const itemId = spItemUrlparts[spItemUrlparts.length - 1].split('?')[0];
                    const driveId = spItemUrlparts[spItemUrlparts.length - 3];

                    return {
                      id: itemId,
                      name: row.FileLeafRef,
                      level: level + 1,
                      driveId,
                      sharePointUniqueId: row.UniqueId.replace("{", "").replace("}", ""),
                      contentTypeId: row.ContentTypeId,
                      path: row.FileRef,
                      canExpand: folderChildCount > 0,
                      expanded: false,
                      isLoading: false,
                      webUrl: currDirectory.webUrl,
                      sharePointListId: currDirectory.sharePointListId,
                      treeType: DirectoryTreeType.folder,
                      randomId: generateRandomString(),
                      canRefresh: true
                    }
                  })
                }
                setLocationsChannelUpdate(channel, undefined, folders, foldersNextHref, false, isRefresh);
              } catch (error) {
                logger(error, "TessAccounts.tsx (renderChannels(getListAsStreamTree))");
              }
              setLocationItem(channel, undefined, false);
            }
            else if ((!channel.expanded && channel.folders?.length > 0) || channel.expanded) {
              setLocationsToggleExpanded(channel);
            }
          }}
        />
        {channel.folders && channel.expanded && renderFolders(channel.folders, channel)}
      </div>
    ));
  };

  const renderFolders = (folders: IFolderLocation[], location: any) => {
    return (
      <div>
        {folders.map((folder, index) => (
          <div key={`${folder.id}-${index}`}>
            <SharePointFolderItem
              key={index}
              treeItem={folder}
              isMobile={isMobile()}
              hasFileSelected={hasFileSelected}
              isValidDropZone={isValidDropZone}
              allowScroll
              getTreeLocations={getTreeLocations}
              onTreeItemClick={onTreeItemClick}
              onSaveClicked={onFolderSaveClicked}
              openFolderOptionsTree={async (e) => {
                e.stopPropagation();
                setLocationItem(folder, undefined, undefined, true);
                setFolderOptionPanelInfo(await openTreeFolderOptions(tessAccountLocations, folder, user));
                setLocationItem(folder, undefined, undefined, false);
              }}
              onTreeExpand={async (isRefresh) => {
                if (isRefresh || (!folder.expanded && !folder.loaded)) {
                  setLocationItem(folder, undefined, true);
                  try {
                    const newWorkingDirectories = findWorkingDirectories(tessAccountLocations, folder);
                    const { directories } = await buildWorkingDirectoryFromTree(newWorkingDirectories, user);
                    const currDirectory = directories[directories.length - 1];

                    const level = folder.level;
                    let subFolders: IFolderLocation[] = [];

                    const results = await getListAsStreamTree({
                      siteUrl: folder.webUrl,
                      sharePointListId: folder.sharePointListId,
                      accessToken: await authModule.getSharePointAccessToken(),
                      nextPageUrl: "",
                      serverRelativePath: folder.path
                    });

                    const foldersNextHref = results?.data?.ListData?.NextHref ? results?.data?.ListData?.NextHref : "";

                    if (results?.data?.ListData?.Row && results?.data?.ListData?.Row.length > 0) {
                      subFolders = results?.data?.ListData?.Row.map(row => {
                        const folderChildCount = row.FolderChildCount ? +row.FolderChildCount : 0;

                        const spItemUrlparts = row['.spItemUrl'].split('/');
                        const itemId = spItemUrlparts[spItemUrlparts.length - 1].split('?')[0];
                        const driveId = spItemUrlparts[spItemUrlparts.length - 3];

                        return {
                          id: itemId,
                          name: row.FileLeafRef,
                          level: level + 1,
                          driveId,
                          sharePointUniqueId: row.UniqueId.replace("{", "").replace("}", ""),
                          contentTypeId: row.ContentTypeId,
                          path: row.FileRef,
                          canExpand: folderChildCount > 0,
                          expanded: false,
                          isLoading: false,
                          webUrl: currDirectory.webUrl,
                          sharePointListId: currDirectory.sharePointListId ?? folder.sharePointListId,
                          treeType: DirectoryTreeType.folder,
                          randomId: generateRandomString(),
                          canRefresh: true
                        }
                      })
                    }

                    setLocationsSiteSubsiteUpdate(folder, undefined, undefined, subFolders, foldersNextHref, false, isRefresh, undefined);

                  } catch (error) {
                    logger(error, "SharePointSite.ts (renderFolders(getListAsStreamTree))");
                  }
                  setLocationItem(folder, undefined, false);
                }
                else if ((!folder.expanded && folder.folders?.length > 0) || folder.expanded) {
                  setLocationsToggleExpanded(folder);
                }
              }}
            />

            {index === folders.length - 1 && location.foldersNextHref && <div style={{ paddingLeft: (location.level ? location.level + 1 : 1) * 15 + 20 }}>
              {!location.isLoading && <ActionButton iconProps={{ iconName: "Table" }} allowDisabledFocus onClick={async (e: any) => {
                e.stopPropagation();
                setLocationItem(location, undefined, true);
                const newWorkingDirectories = findWorkingDirectories(tessAccountLocations, location);
                const { directories } = await buildWorkingDirectoryFromTree(newWorkingDirectories, user);
                const currDirectory = directories[directories.length - 1];

                try {
                  const level = location.level ? location.level : 0;
                  let folders: IFolderLocation[] = [];

                  const inOneDrive = location.isOneDrive;
                  const webUrl = currDirectory.webUrl;
                  const sharePointListId = currDirectory.sharePointListId;

                  const results = await getListAsStreamTree({
                    siteUrl: webUrl,
                    sharePointListId,
                    accessToken: await authModule.getSharePointAccessToken(),
                    nextPageUrl: location.foldersNextHref,
                    serverRelativePath: currDirectory.serverRelativePath
                  });

                  const foldersNextHref = results?.data?.ListData?.NextHref ? results?.data?.ListData?.NextHref : "";

                  if (results?.data?.ListData?.Row && results?.data?.ListData?.Row.length > 0) {
                    folders = results?.data?.ListData?.Row.map(row => {
                      const spItemUrlparts = row['.spItemUrl'].split('/');
                      const itemId = spItemUrlparts[spItemUrlparts.length - 1].split('?')[0];
                      const driveId = spItemUrlparts[spItemUrlparts.length - 3];

                      return {
                        id: itemId,
                        name: row.FileLeafRef,
                        level: level + 1,
                        driveId,
                        sharePointUniqueId: row.UniqueId.replace("{", "").replace("}", ""),
                        contentTypeId: row.ContentTypeId,
                        path: row.FileRef,
                        canExpand: true,
                        expanded: false,
                        isLoading: false,
                        webUrl,
                        sharePointListId,
                        isOneDrive: inOneDrive,
                        treeType: DirectoryTreeType.folder,
                        randomId: generateRandomString(),
                        canRefresh: true
                      }
                    })
                  }

                  setLocationsSiteSubsiteUpdate(location, undefined, undefined, [...location.folders, ...folders], foldersNextHref, true, false, undefined);

                } catch (error) {
                  logger(error, "TessAccounts.tsx (renderChannels(getListAsStreamTree))");
                  setLocationItem(location, undefined, false);
                }
              }}>
                {languageStrings.LoadMore}
              </ActionButton>}

            </div>}
            {index === folders.length - 1 && location.foldersNextHref && location.isLoading && <div style={{ paddingTop: "10px" }}>
              <Spinner size={SpinnerSize.small} />
            </div>}

            {folder.folders && folder.expanded && renderFolders(folder.folders, folder)}
          </div>
        ))}
      </div>
    )
  };

  const dismissFolderOptionPanel = React.useCallback(() => {
    setFolderOptionPanelInfo(initialFolderOptionPanelInfoSetting);
  }, []);

  return isLoading || tessAuthHook?.isTessRequestInProgress ? (
    <Loader hide={hide} />
  ) : (
    <div className={hide ? "none" : (!isGeneralSearchRequest ? "fullHeight" : undefined)}>
      {(!searchValue || (!isSearchingContent && !isSearchingFilesAndFolders)) && <ExpanderWithSubtitle
        items={accountItems?.length > 0
          ? accountItems
          : [<div key="NoResultsFound" style={{ textAlign: "center" }}>
            <Label>
              {languageStrings.GenericNoResultsFoundText}
            </Label>
          </div>]}
        title={"Accounts"}
        expanderNameToToggle={expanderConstants.sharepoint.sites}
        hasShowMoreButton={searchValue && isFileOrFolderSearchResultsAvailable}
        fixedHeight={locationHeight}
        isWaitingOnContent={isSearching || isReloading}
        allowScroll={allowSiteScroll.current}
        actionButton={generalModule.isInConentMangerMode()
          ? <SubTitleRefreshButton
            onClick={async () => {
              if (tessAuthHook) {
                setIsReloading(true);
                await tessAuthHook?.loadTessAccounts(false);
                setIsReloading(false);
              }
            }}
          />
          : tessAuthHook?.allowLoadAllAccounts && <TooltipHost content={tessAuthHook.isAllAccountsLoaded ? languageStrings.TessShowContactAccounts : languageStrings.TessShowAllAccounts}>
            <ActionButton
              iconProps={{ iconName: tessAuthHook.isAllAccountsLoaded ? "Contact" : "ContactList", styles: iconStyles }}
              styles={{ root: { padding: 0, height: 25 } }}
              allowDisabledFocus
              onClick={async (event) => {
                event.stopPropagation();
                setIsReloading(true);
                await tessAuthHook?.toggleAllTessAccounts(!tessAuthHook.isAllAccountsLoaded);
                setIsReloading(false);
              }}
            />
          </TooltipHost>
        }
      />}
      {folderOptionPanelInfo?.isOpen && <FolderOptionPanel info={folderOptionPanelInfo} dismissPanel={dismissFolderOptionPanel} />}
      {!hide && !isGeneralSearchRequest && searchValue && <FileAndFoldersSharepointSearch parentViewHasItems={isSearching || accountItems?.length > 0} isSearchingCallback={(isSearching: boolean) => setIsSearchingContent(isSearching)} searchResultsAvailableCallback={UpdateIsFileOrFolderSearchResultsAvailable} isLoadingCallback={(isLoading: boolean) => setIsLoading(isLoading)} />}
    </div>
  );
};

export default React.memo(connector(TessAccounts));
